import React from 'react';
import { Globe, Handshake, Leaf, MapPin } from 'lucide-react';

const TreePlantingSection = () => {
  const features = [
    {
      icon: <Globe className="h-6 w-6 text-green-400" />,
      title: "Impact Global",
      description: "Nos projets de reforestation s'étendent sur 4 continents, créant un impact environnemental à l'échelle mondiale.",
      color: "bg-emerald-500/10"
    },
    {
      icon: <Handshake className="h-6 w-6 text-green-400" />,
      title: "Partenariats Stratégiques",
      description: "Nous soutenons des associations spécialisées dans la reforestation, garantissant la qualité et la pérennité de chaque projet.",
      color: "bg-blue-500/10"
    },
    {
      icon: <Leaf className="h-6 w-6 text-green-400" />,
      title: "Diversité Écologique",
      description: "Chaque zone de plantation est choisie pour ses bénéfices environnementaux uniques et sa contribution à la biodiversité.",
      color: "bg-teal-500/10"
    },
    {
      icon: <MapPin className="h-6 w-6 text-green-400" />,
      title: "Flexibilité Totale",
      description: "Vous choisissez parmi une multitude d'options de plantation, adaptées à vos préférences et à vos valeurs.",
      color: "bg-indigo-500/10"
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            Une Reforestation Adaptée à Chaque Projet
          </h2>
          <p className="text-xl text-slate-600">
            Nous proposons une multitude d'options de plantation, en collaboration avec des associations 
            spécialisées dans la reforestation, pour maximiser l'impact environnemental de chaque projet.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div key={index} className="bg-slate-50 p-8 rounded-xl border border-slate-200 hover:border-green-200 transition-colors">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className={`${feature.color} p-4 rounded-lg`}>
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-12 rounded-xl">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-6">
                  Des Partenariats pour un Impact Maximum
                </h3>
                <p className="text-slate-300 mb-8">
                  Notre réseau de partenaires spécialisés nous permet d'offrir 
                  une grande variété d'options de plantation, chacune adaptée à des écosystèmes 
                  spécifiques et à des besoins environnementaux particuliers.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2" />
                    <span className="text-slate-300">Organisations spécialisées en reforestation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2" />
                    <span className="text-slate-300">Associations environnementales reconnues</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2" />
                    <span className="text-slate-300">Experts en gestion forestière</span>
                  </li>
                </ul>
              </div>
              <div className="bg-slate-800/50 p-4 md:p-8 rounded-lg border border-slate-700/50">
                <h4 className="text-xl font-semibold mb-4">Comment ça marche ?</h4>
                <ol className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/10 text-green-400 flex items-center justify-center text-sm font-medium">1</span>
                    <span className="text-slate-300">Nous calculons l'empreinte carbone de votre projet</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/10 text-green-400 flex items-center justify-center text-sm font-medium">2</span>
                    <span className="text-slate-300">Vous choisissez parmi nos options de plantation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/10 text-green-400 flex items-center justify-center text-sm font-medium">3</span>
                    <span className="text-slate-300">Nos partenaires plantent et entretiennent les arbres</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/10 text-green-400 flex items-center justify-center text-sm font-medium">4</span>
                    <span className="text-slate-300">Vous suivez l'impact de votre contribution</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TreePlantingSection; 